import { useEffect } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { chatApi } from '../api/chat.js';
import { schoolFullLabel } from '../lib/format.js';
import { userRoleLabel } from '../lib/labels.js';
import { qk } from '../lib/queryKeys.js';
import { useDemoStatusQuery } from '../hooks/queries.js';
import { useSession } from '../store/session.js';
import { Button } from './Button.js';
import { MobileTabBar } from './MobileTabBar.js';
import { NavIcon, type IconName } from './navIcons.js';
import { SiteFooter } from './SiteFooter.js';
import { useLogout } from '../hooks/useLogout.js';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** 角标（如 AI）：标记 AI 功能入口 */
  badge?: string;
  /** 仅平台管理员可见（管理后台入口；非 admin 直链打开会被后端 403 挡住） */
  adminOnly?: boolean;
  /** 仅指定角色可见（如资讯入口仅组织者/管理员） */
  roles?: ('organizer' | 'admin')[];
}

const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: '发现赛事',
    items: [
      { to: '/events', label: '赛事联动', icon: 'events' },
      { to: '/news', label: '竞赛资讯', icon: 'news', roles: ['organizer', 'admin'], badge: 'AI' },
    ],
  },
  {
    label: '组队中',
    items: [
      { to: '/profile', label: '我的画像', icon: 'profile' },
      { to: '/find', label: '找队友', icon: 'find' },
      { to: '/assistant', label: 'AI 队友', icon: 'assistant', badge: 'AI' },
    ],
  },
  {
    label: '成队后',
    items: [
      { to: '/team', label: '我的队伍', icon: 'team' },
      { to: '/messages', label: '消息', icon: 'messages' },
      { to: '/tasks', label: '待办看板', icon: 'tasks' },
      { to: '/advisors', label: '指导老师', icon: 'advisor' },
    ],
  },
  {
    label: '系统',
    items: [
      { to: '/settings', label: '设置与合规', icon: 'settings' },
      { to: '/admin', label: '管理后台', icon: 'admin', adminOnly: true },
    ],
  },
];

function navClass({ isActive }: { isActive: boolean }): string {
  return isActive ? 'nav-link active' : 'nav-link';
}

/**
 * 数据来源角标（常显）：有演示数据 → 「演示数据」，否则 → 「我的数据」。
 * 数据来自 useDemoStatusQuery（meta.silent + retry:false）：**未登录或请求失败时静默降级**，
 * 只显示「我的数据」，既不弹全局错误 Toast，也不会让布局崩掉。
 */
function DataBadge({ demoCount }: { demoCount: number }) {
  const isDemo = demoCount > 0;
  return (
    <span
      className={isDemo ? 'data-badge demo' : 'data-badge'}
      title={
        isDemo
          ? `候选人池里有 ${demoCount} 条演示数据（demo- 前缀）：到「设置与合规 → 演示模式」可一键清除，不影响你自己录入的候选人`
          : '当前没有演示数据（demo- 前缀）；这里的候选人都来自你自己的录入'
      }
    >
      {isDemo ? '演示数据' : '我的数据'}
    </span>
  );
}

/** 全局布局：桌面侧边导航；移动端（≤860px）顶部品牌条 + 底部标签栏，375px 宽为单列。 */
export function Layout() {
  const user = useSession((state) => state.user);
  const authed = useSession((state) => Boolean(state.accessToken) || Boolean(state.refreshToken));
  const handleLogout = useLogout();
  const demoStatus = useDemoStatusQuery(authed);
  const demoCount = demoStatus.data?.demoCandidates ?? 0;

  // 消息未读角标：30s 轻量轮询 /conversations/unread，失败静默降级（不打扰）
  const unreadQuery = useQuery({
    queryKey: qk.chatUnread(),
    queryFn: () => chatApi.unreadTotal(),
    enabled: authed,
    refetchInterval: 30_000,
    retry: 0,
    meta: { silent: true },
  });
  const unread = unreadQuery.data?.totalUnread ?? 0;

  // 管理后台入口只对 admin 显示；资讯入口只对 organizer/admin 显示（角色过滤在这里做一次）
  const visibleGroups = NAV_GROUPS.map((group) => ({
    label: group.label,
    items: group.items.filter(
      (item) =>
        (item.adminOnly !== true || user?.role === 'admin') &&
        (item.roles === undefined || (user?.role !== undefined && (item.roles as readonly string[]).includes(user.role))),
    ),
  }));
  const visibleItems = visibleGroups.flatMap((group) => group.items);

  // 鼠标光晕跟随（借鉴 v4.1 参考稿）：pointer:fine 设备才启用，弱网/触屏零开销
  useEffect(() => {
    const glow = document.getElementById('mouseGlow');
    if (glow === null || !window.matchMedia('(pointer: fine)').matches) return;
    const move = (event: MouseEvent): void => {
      glow.style.left = `${event.clientX}px`;
      glow.style.top = `${event.clientY}px`;
    };
    window.addEventListener('mousemove', move, { passive: true });
    return () => window.removeEventListener('mousemove', move);
  }, []);

  return (
    <div className="app-shell">
      <div id="mouseGlow" aria-hidden="true" />
      <div className="bg-orbs" aria-hidden="true">
        <div className="bg-orb" />
        <div className="bg-orb" />
        <div className="bg-orb" />
      </div>
      <aside className="sidebar">
        <Link to="/home" className="brand brand-link">
          <span className="brand-mark" aria-hidden="true">
            CM
          </span>
          <div className="brand-text">
            <div className="brand-name">CrossMatch</div>
            <div className="brand-sub">松江七校跨校组队引擎</div>
          </div>
        </Link>

        <nav className="nav" aria-label="主导航">
          {visibleGroups.map((group) => (
            <div key={group.label} className="nav-group">
              <div className="nav-group-label" aria-hidden="true">
                {group.label}
              </div>
              {group.items.map((item) => (
                <NavLink key={item.to} to={item.to} className={navClass}>
                  <NavIcon name={item.icon} />
                  <span>{item.label}</span>
                  {item.to === '/messages' && unread > 0 ? (
                    <span className="nav-badge">{unread > 99 ? '99+' : unread}</span>
                  ) : item.badge ? (
                    <span className="nav-badge">{item.badge}</span>
                  ) : null}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-foot">
          <DataBadge demoCount={demoCount} />
          <div className="who">
            <div className="who-name">{user?.name ?? '未登录'}</div>
            <div className="who-meta" title={user ? `${schoolFullLabel(user.school)} · ${user.email}` : undefined}>
              {user ? `${schoolFullLabel(user.school)} · ${userRoleLabel(user.role)}` : '请先登录'}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            退出登录
          </Button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <div className="row">
            <span className="brand-mark" aria-hidden="true">
              CM
            </span>
            <div className="grow">
              <div className="brand-name">CrossMatch</div>
              <div className="brand-sub">{user ? `${user.name} · ${schoolFullLabel(user.school)}` : '未登录'}</div>
            </div>
            <DataBadge demoCount={demoCount} />
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              退出
            </Button>
          </div>
          <nav className="topbar-nav" aria-label="移动端主导航">
            {visibleItems.map((item) => (
              <NavLink key={item.to} to={item.to} className={navClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </header>
        <main className="page">
          <Outlet />
        </main>
        <SiteFooter />
      </div>

      <MobileTabBar />
    </div>
  );
}